'use client';
import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import GymPortalLoginForm from './GymPortalLoginForm';
import MemberPortalLoginForm from './MemberPortalLoginForm';
import ForgotPinFlow from './ForgotPinFlow';
import SignupForm from './SignupForm';
import { Building2, Users, Lock, Clock, ShieldCheck, TrendingUp, QrCode, ArrowRight, Shield, Zap, CheckCircle } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';



type AuthView = 'select' | 'gym-login' | 'member-login' | 'forgot-pin' | 'signup';

const features = [
  {
    icon: Clock,
    title: 'Real-time attendance tracking',
    desc: 'Track and manage attendance in real time.',
    accent: '#AAFF00',
  },
  {
    icon: ShieldCheck,
    title: 'Secure member management',
    desc: 'Keep member data safe and organized.',
    accent: '#FF3B30',
  },
  {
    icon: TrendingUp,
    title: 'Revenue & expense analytics',
    desc: 'Gain insights and grow your gym profitably.',
    accent: '#AAFF00',
  },
  {
    icon: QrCode,
    title: 'Multi-branch QR check-in',
    desc: 'Manage multiple branches with ease.',
    accent: '#FF3B30',
  },
];

const trustItems = [
  { icon: Shield, label: 'Secure', sub: 'Your data is protected' },
  { icon: Zap, label: 'Fast', sub: 'Built for performance' },
  { icon: CheckCircle, label: 'Reliable', sub: 'Always here for you' },
];

function PortalSelectionScreen({
  onSelectGym,
  onSelectMember,
  onSelectAdmin,
}: {
  onSelectGym: () => void;
  onSelectMember: () => void;
  onSelectAdmin: () => void;
}) {
  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-8">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center justify-center gap-2.5 mb-6">
          <AppLogo size={36} />
          <div className="text-left">
            <div className="font-display text-xl text-foreground tracking-wider leading-none">GYM VRIDHI</div>
            <div className="text-[10px] text-muted-foreground tracking-[0.15em] uppercase mt-0.5">Premium Gym Management</div>
          </div>
        </div>

        {/* Dumbbell icon */}
        <div className="flex items-center justify-center mb-4">
          <svg width="32" height="16" viewBox="0 0 32 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="5" width="4" height="6" rx="1.5" fill="#AAFF00" opacity="0.7"/>
            <rect x="4" y="3" width="3" height="10" rx="1.5" fill="#AAFF00" opacity="0.9"/>
            <rect x="7" y="7" width="18" height="2" rx="1" fill="#AAFF00" opacity="0.5"/>
            <rect x="25" y="3" width="3" height="10" rx="1.5" fill="#AAFF00" opacity="0.9"/>
            <rect x="28" y="5" width="4" height="6" rx="1.5" fill="#AAFF00" opacity="0.7"/>
          </svg>
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-1.5 tracking-tight">
          Welcome to <span className="text-secondary">GYM VRIDHI</span>
        </h2>
        <p className="text-sm text-muted-foreground">Choose your portal to continue</p>
      </div>

      {/* Portal Cards */}
      <div className="space-y-3 mb-5">
        {/* Gym Portal */}
        <button
          type="button"
          onClick={onSelectGym}
          className="w-full group relative flex items-center gap-4 px-5 py-5 rounded-2xl text-left transition-all duration-300 active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255,59,48,0.08) 0%, rgba(24,31,27,1) 60%)',
            border: '1px solid rgba(255,59,48,0.2)',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,59,48,0.5)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 24px rgba(255,59,48,0.15), inset 0 0 40px rgba(255,59,48,0.04)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,59,48,0.2)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
          }}
        >
          {/* Icon */}
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105"
            style={{ background: 'linear-gradient(135deg, rgba(255,59,48,0.25) 0%, rgba(255,59,48,0.1) 100%)' }}
          >
            <Building2 size={24} className="text-primary" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-foreground text-lg leading-tight">Gym Portal</div>
            <div className="text-sm text-muted-foreground mt-0.5">Owner <span className="text-primary/60">•</span> Staff <span className="text-primary/60">•</span> Trainer</div>
            <div
              className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-lg text-xs text-muted-foreground"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <Building2 size={10} className="text-muted-foreground" />
              Manage your gym business
            </div>
          </div>

          {/* Arrow */}
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 group-hover:scale-110"
            style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #cc2e24 100%)' }}
          >
            <ArrowRight size={16} className="text-white" />
          </div>
        </button>

        {/* Member Portal */}
        <button
          type="button"
          onClick={onSelectMember}
          className="w-full group relative flex items-center gap-4 px-5 py-5 rounded-2xl text-left transition-all duration-300 active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(170,255,0,0.06) 0%, rgba(24,31,27,1) 60%)',
            border: '1px solid rgba(170,255,0,0.18)',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(170,255,0,0.45)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 24px rgba(170,255,0,0.12), inset 0 0 40px rgba(170,255,0,0.03)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(170,255,0,0.18)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
          }}
        >
          {/* Icon */}
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105"
            style={{ background: 'linear-gradient(135deg, rgba(170,255,0,0.2) 0%, rgba(170,255,0,0.08) 100%)' }}
          >
            <Users size={24} className="text-secondary" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-foreground text-lg leading-tight">Member Portal</div>
            <div className="text-sm text-muted-foreground mt-0.5">Gym Members</div>
            <div
              className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-lg text-xs text-muted-foreground"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <Users size={10} className="text-muted-foreground" />
              Access your membership
            </div>
          </div>

          {/* Arrow */}
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 group-hover:scale-110"
            style={{ background: 'linear-gradient(135deg, #AAFF00 0%, #88cc00 100%)' }}
          >
            <ArrowRight size={16} className="text-[#0F1210]" />
          </div>
        </button>
      </div>

      {/* Divider + Admin */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px" style={{ background: 'rgba(42,53,48,0.8)' }} />
        <span className="text-xs text-muted-foreground px-1">or</span>
        <div className="flex-1 h-px" style={{ background: 'rgba(42,53,48,0.8)' }} />
      </div>

      <div className="flex justify-center mb-6">
        <button
          type="button"
          onClick={onSelectAdmin}
          className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-white/5"
        >
          <Lock size={12} className="text-muted-foreground group-hover:text-foreground transition-colors" />
          <span className="text-[13px] text-muted-foreground group-hover:text-foreground transition-colors">Admin Login</span>
        </button>
      </div>

      {/* Trust indicators */}
      <div className="flex items-center justify-center gap-6">
        {trustItems.map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={`trust-${i}`} className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-1.5">
                <Icon size={13} className="text-secondary opacity-70" />
                <span className="text-xs font-semibold text-foreground/80">{item.label}</span>
              </div>
              <span className="text-[10px] text-muted-foreground text-center leading-tight">{item.sub}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function LoginPageClient() {
  const [view, setView] = useState<AuthView>('select');
  const { profile, loading: authLoading } = useAuth();

  // If user is already authenticated, redirect to their dashboard
  useEffect(() => {
    if (authLoading || !profile) return;
    const roleRoutes: Record<string, string> = {
      member: '/member-dashboard',
      owner: '/owner-dashboard',
      staff: '/owner-dashboard',
      trainer: '/owner-dashboard',
      super_admin: '/admin/dashboard',
    };
    const destination = roleRoutes[profile.role];
    if (destination) {
      window.location.replace(destination);
    }
  }, [profile, authLoading]);

  return (
    <div className="min-h-screen flex" style={{ background: '#0F1210' }}>
      {/* ── LEFT PANEL — Brand / Hero ── */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col justify-between relative overflow-hidden" style={{ padding: '40px 48px' }}>
        {/* Gym background photo at very low opacity */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=80&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.07,
          }}
        />

        {/* Gradient overlays for depth */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,59,48,0.08) 0%, transparent 50%, rgba(170,255,0,0.05) 100%)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
          style={{ background: 'linear-gradient(to top, #0F1210 0%, transparent 100%)' }}
        />
        <div
          className="absolute top-0 left-0 w-[500px] h-[500px] pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,59,48,0.1) 0%, transparent 65%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute bottom-20 right-0 w-[400px] h-[400px] pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(170,255,0,0.07) 0%, transparent 65%)',
            filter: 'blur(60px)',
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <AppLogo size={44} />
          <div>
            <div className="font-display text-2xl text-foreground tracking-wider leading-none">GYM VRIDHI</div>
            <div className="text-[10px] text-muted-foreground tracking-[0.18em] uppercase mt-1">Premium Gym Management</div>
          </div>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 flex-1 flex flex-col justify-center py-8">
          <h1 className="text-hero text-foreground mb-6 leading-none">
            RUN YOUR<br />
            <span style={{ color: '#AAFF00' }}>GYM</span><br />
            SMARTER<span style={{ color: '#FF3B30' }}>.</span>
          </h1>

          <p className="text-base text-muted-foreground max-w-[340px] mb-8 leading-relaxed" style={{ fontSize: '15px' }}>
            Everything a gym owner needs — members, attendance, payments, classes, and analytics — in one powerful platform.
          </p>

          {/* Feature cards — 2×2 grid */}
          <div className="grid grid-cols-2 gap-3 max-w-[420px]">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={`feat-${i}`}
                  className="group flex flex-col gap-2 p-4 rounded-2xl transition-all duration-200 cursor-default"
                  style={{
                    background: 'rgba(24,31,27,0.7)',
                    border: '1px solid rgba(42,53,48,0.8)',
                    backdropFilter: 'blur(8px)',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = `${f.accent}30`;
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(42,53,48,0.8)';
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${f.accent}15` }}
                  >
                    <Icon size={16} style={{ color: f.accent }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground leading-tight mb-1">{f.title}</div>
                    <div className="text-xs text-muted-foreground leading-snug">{f.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center gap-3">
          <Shield size={13} className="text-muted-foreground opacity-50" />
          <span className="text-xs text-muted-foreground">© 2026 GYM VRIDHI</span>
          <span className="text-muted-foreground opacity-40">•</span>
          <a href="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy</a>
          <span className="text-muted-foreground opacity-40">•</span>
          <a href="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms</a>
        </div>
      </div>

      {/* ── RIGHT PANEL — Auth ── */}
      <div
        className="w-full lg:w-[48%] xl:w-[45%] flex flex-col items-center justify-center p-5 sm:p-8 relative min-h-screen lg:min-h-0"
        style={{ borderLeft: '1px solid rgba(42,53,48,0.6)' }}
      >
        {/* Subtle right panel background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(160deg, rgba(24,31,27,0.4) 0%, transparent 60%)' }}
        />

        <div className="w-full max-w-[400px] relative z-10 fade-in">
          {view === 'select' && (
            <PortalSelectionScreen
              onSelectGym={() => setView('gym-login')}
              onSelectMember={() => setView('member-login')}
              onSelectAdmin={() => { window.location.href = '/admin'; }}
            />
          )}

          {view === 'gym-login' && (
            <GymPortalLoginForm
              onForgotPin={() => setView('forgot-pin')}
              onSignup={() => setView('signup')}
              onBack={() => setView('select')}
            />
          )}

          {view === 'member-login' && (
            <MemberPortalLoginForm
              onForgotPin={() => setView('forgot-pin')}
              onBack={() => setView('select')}
            />
          )}

          {view === 'forgot-pin' && (
            <ForgotPinFlow onBack={() => setView('select')} />
          )}

          {view === 'signup' && (
            <SignupForm onBack={() => setView('gym-login')} />
          )}
        </div>

        {/* Mobile footer */}
        <div className="lg:hidden mt-6 flex items-center justify-center gap-3 relative z-10 pb-safe">
          <span className="text-xs text-muted-foreground">© 2026 GYM VRIDHI</span>
          <span className="text-muted-foreground opacity-40">•</span>
          <a href="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy</a>
          <span className="text-muted-foreground opacity-40">•</span>
          <a href="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms</a>
        </div>
      </div>
    </div>
  );
}